import { Timestamp } from 'firebase/firestore';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useOrganizationState } from '@/state';
import { useLocaleNavigate } from '@/utils/locale';

import { MobileTrialBarWrapper, TrialBadge, TrialCountdown, TrialLabel } from './MobileTrialBar.styled';

const getTestModeCountdown = (expirationDate: Timestamp | null): string | null => {
  if (!expirationDate) return null;

  const now = Date.now();
  const expMs = expirationDate.toMillis();
  const diff = expMs - now;

  if (diff <= 0) return null;

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

  if (days > 0) return `${days}d ${hours}h`;
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  return `${hours}h ${minutes}m`;
};

const getTestModeUrgency = (expirationDate: Timestamp | null): 'normal' | 'warning' | 'critical' => {
  if (!expirationDate) return 'normal';

  const now = Date.now();
  const expMs = expirationDate.toMillis();
  const diffMs = expMs - now;

  if (diffMs <= 0) return 'critical';

  const ONE_DAY = 1000 * 60 * 60 * 24;
  if (diffMs <= ONE_DAY) return 'critical';
  if (diffMs <= 3 * ONE_DAY) return 'warning';
  return 'normal';
};

export const MobileTrialBar: FC = () => {
  const { t } = useTranslation();
  const navigate = useLocaleNavigate();
  const { plan, testExpirationDate } = useOrganizationState();

  const isTestMode = plan === 'TEST';

  const testCountdown = useMemo(
    () => getTestModeCountdown(testExpirationDate),
    [testExpirationDate],
  );

  const testUrgency = useMemo(
    () => getTestModeUrgency(testExpirationDate),
    [testExpirationDate],
  );

  if (!isTestMode || !testCountdown) return null;

  return (
    <MobileTrialBarWrapper>
      <TrialBadge $urgency={testUrgency}>
        <TrialLabel $urgency={testUrgency}>{t('layout.trialPeriodRemaining')}</TrialLabel>
        <TrialCountdown $urgency={testUrgency}>{testCountdown}</TrialCountdown>
      </TrialBadge>
    </MobileTrialBarWrapper>
  );
};
